import {
  FormControlProps,
  HStack,
  IconButton,
  InputProps,
  NumberInput,
  NumberInputField,
  useNumberInput,
} from "@chakra-ui/react";
import { createRef, FormEvent, RefObject } from "react";
import { Controller, ControllerProps, ControllerRenderProps, FieldPath, FieldValues } from "react-hook-form";

import { useIsTouchDevice } from "../../../hooks";
import { ControlledFormControl } from "../../ControlledFormControl/ControlledFormControl";
import { MinusIcon, PlusIcon } from "../../Icons/Icons";

export interface NumberControlledInputOptions<T extends FieldValues, TName extends FieldPath<T>> {
  isDisabled?: boolean;

  label?: React.ReactNode;

  render?: (
    properties: Omit<ControllerRenderProps<T, TName>, "ref"> &
      Pick<InputProps, "isReadOnly" | "placeholder" | "rounded">,
    reference: React.RefObject<HTMLInputElement> | undefined,
  ) => React.ReactNode;
}

export interface NumberControlledInputProperties<T extends FieldValues, TName extends FieldPath<T>>
  extends NumberControlledInputOptions<T, TName>,
    Pick<ControllerProps<T, TName>, "control" | "defaultValue" | "name" | "rules">,
    Omit<FormControlProps, "defaultValue" | "label"> {
  inputRef?: React.RefObject<HTMLInputElement>;
  max?: number;

  min?: number;
  placeholder?: string;
}

/**
 * To use this input you have to wrap your form with the FormProvider
 */
export const NumberControlledInput = <T extends FieldValues, TName extends FieldPath<T>>({
  control,
  inputRef: inputReference = createRef<HTMLInputElement>(),
  isDisabled,
  label,
  max,
  min,
  name,
  placeholder,
  render = ({ isReadOnly, onChange, placeholder, rounded, ...rest }, reference) => (
    <NumberInput
      flexGrow={1}
      min={min}
      max={max}
      onChange={(s, v) => {
        onChange(Number.isNaN(v) ? (s ? undefined : 0) : v);
      }}
      {...rest}
    >
      <NumberInputField
        _groupHover={{
          borderBottomColor: "blue.300",
          borderTopColor: "blue.300",
        }}
        _hover={{
          bg: "blue.50",
        }}
        bg="white"
        borderLeftWidth={isReadOnly ? undefined : 0}
        borderRadius={0}
        borderRightWidth={isReadOnly ? undefined : 0}
        data-testid="NumberControlledInput-input"
        p={0}
        placeholder={placeholder}
        ref={reference}
        rounded={rounded}
        textAlign="center"
      />
    </NumberInput>
  ),
  rules,
  textAlign,
  ...rest
}: NumberControlledInputProperties<T, TName>): React.ReactElement => {
  const { isReadOnly } = rest;

  return (
    <ControlledFormControl
      control={control}
      isDisabled={isDisabled}
      isRequired={!!rules?.required}
      label={label}
      name={name}
      textAlign={textAlign ?? "center"}
      {...rest}
    >
      <Controller
        control={control}
        name={name}
        render={({ field }) => {
          const { ref, ...withoutReference } = field;
          void ref;
          return (
            <NumberInputWrapper
              inputRef={inputReference}
              isDisabled={isDisabled}
              isReadOnly={isReadOnly}
              max={max}
              min={min}
              onChange={(value: number) => {
                // field onChange must be first
                field.onChange(value);
                rest.onChange?.({
                  target: { value },
                } as unknown as FormEvent<HTMLDivElement>);
              }}
              value={field.value}
            >
              {render(
                {
                  isReadOnly,
                  placeholder: placeholder ?? (typeof label === "string" ? label : undefined),
                  rounded: isReadOnly ? "md" : "none",
                  ...withoutReference,
                },
                inputReference,
              )}
            </NumberInputWrapper>
          );
        }}
        rules={rules}
      />
    </ControlledFormControl>
  );
};

interface NumberInputWrapperProperties {
  children?: React.ReactNode;
  inputRef?: RefObject<HTMLInputElement>;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  max?: number;
  min?: number;
  onChange: (value: number) => void;
  size?: string;
  value: number;
}

export const NumberInputWrapper = ({
  children,
  inputRef,
  isDisabled,
  isReadOnly,
  max,
  min,
  onChange,
  size,
  value,
}: NumberInputWrapperProperties) => {
  const isTouch = useIsTouchDevice();
  const { getDecrementButtonProps, getIncrementButtonProps } = useNumberInput({
    isDisabled,
    isReadOnly,
    max,
    min,
    onChange: (_, v) => {
      const { activeElement } = document;
      // Lose focus if mobile without keyboard open
      if (
        isTouch &&
        activeElement instanceof HTMLElement &&
        !(activeElement instanceof HTMLInputElement) &&
        !(activeElement instanceof HTMLTextAreaElement)
      ) {
        activeElement.blur();
      } else {
        inputRef?.current?.focus();
      }
      onChange(v);
    },
    value,
  });

  return (
    <HStack role="group" spacing="0" w="full">
      {!isReadOnly && (
        <IconButton
          _groupHover={{
            borderBottomColor: "blue.300",
            borderLeftColor: "blue.300",
            borderTopColor: "blue.300",
          }}
          aria-label="number input minus"
          colorScheme="gray"
          data-testid="NumberControlledInput-minus"
          roundedLeft="md"
          roundedRight="none"
          size={size}
          variant="outline"
          {...getDecrementButtonProps()}
        >
          <MinusIcon />
        </IconButton>
      )}
      {children}
      {!isReadOnly && (
        <IconButton
          _groupHover={{
            borderBottomColor: "blue.300",
            borderRightColor: "blue.300",
            borderTopColor: "blue.300",
          }}
          aria-label="number input plus"
          colorScheme="gray"
          data-testid="NumberControlledInput-plus"
          roundedLeft="none"
          roundedRight="md"
          size={size}
          variant="outline"
          {...getIncrementButtonProps()}
        >
          <PlusIcon />
        </IconButton>
      )}
    </HStack>
  );
};
